import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { FiExternalLink } from "react-icons/fi";
import { ChatIcon } from "../../icons/ChatIcon";

import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import { useAppStore } from "../../../hooks/useAppStore";
import useNotifier from "../../../hooks/useNotifier";
import CoreVirtualizedTable from "../../ui/core/CoreVirtualizedTable";
import Row from "../../ui/core/Row";
import {
  IPolicyPreviewItem,
  POLICY_RESEARCH_TABLE_COLUMN_OPTIONS,
  PolicyResearchTableColumnId,
  PolicyStatus,
} from "../../../types/chat";
import {
  ColumnFiltersConfig,
  ColumnFilterType,
  SimpleColumnDef,
  SortingState,
} from "../../../types/table";
import { STATE_CODE_TO_NAME } from "../../../config";
import { toStartCase } from "../../../utils";
import { PolicyTypeBadge } from "./PolicyTypeBadge";
import StatusChip from "../../claims/components/table-components/StatusChip";

// Columns that get an Excel-like header filter popover -- backend-driven (ChatCategoryStore.setPolicyResearchColumnFilter /
// clearPolicyResearchColumnFilter -> fetchPolicyResearch), same pattern as ContractResearchTable.
// Keep in sync with app-backend's ElasticsearchService.POLICY_COLUMN_FILTER_FIELDS/POLICY_COLUMN_FILTER_TYPES.
const FILTERABLE_COLUMNS = [
  "title",
  "status",
  "state",
  "payer",
  "plan_name",
  "plan_policy_id",
  "line_of_business",
  "document_type",
  "date_of_business",
  "expiration_date",
  "updated_on",
  "hcpcs_codes",
  "icd_10_codes",
];

const COLUMN_FILTER_TYPES: Record<string, ColumnFilterType> = {
  title: "text",
  payer: "text",
  plan_name: "text",
  plan_policy_id: "text",
  date_of_business: "dateRange",
  expiration_date: "dateRange",
  updated_on: "dateRange",
  hcpcs_codes: "text",
  icd_10_codes: "text",
};

// The backend matches code columns on whole codes, not substrings, so "J35" alone matches nothing.
const COLUMN_FILTER_PLACEHOLDERS: Record<string, string> = {
  hcpcs_codes: "Whole code, e.g. J3590",
  icd_10_codes: "Whole code, e.g. M17.11",
};

// status is derived server-side, so offer every value rather than only those on loaded rows.
const POLICY_STATUSES: PolicyStatus[] = ["active", "upcoming", "expired"];

const renderCodes = (value?: string[]) => {
  const label = value?.length ? value.join(", ") : "—";
  return (
    <Typography noWrap title={label}>
      {label}
    </Typography>
  );
};

const OpenInNewIcon = FiExternalLink as any;

export const PolicyQuestionsTable = observer(() => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isLoadingPolicyResearch,
    policyResearchData,
    policyResearchIsLoadingMore,
    policyResearchHasMoreData,
    fetchPolicyResearch,
    policyResearchSort,
    setPolicyResearchSorting,
    policyResearchColumnFilters,
    setPolicyResearchColumnFilter,
    clearPolicyResearchColumnFilter,
    setFilters,
    setIsPoliciesDocumentsDrawerOpen,
    setIsAskingReveForPolicy,
    isPolicyResearchColumnVisible,
    isPolicyResearchColumnRequired,
    togglePolicyResearchRequiredColumn,
    policyResearchColumnSettings,
  } = useChatCategoryStore();
  const { setPolicyToPreview } = useAppStore();

  const [tableHeight, setTableHeight] = useState<number>(500);
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const notify = useNotifier();

  const sorting: SortingState = useMemo(
    () =>
      policyResearchSort.by
        ? [
            {
              id: policyResearchSort.by,
              desc: policyResearchSort.direction === "desc",
            },
          ]
        : [],
    [policyResearchSort],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      const [sortConfig] = newSorting;
      setPolicyResearchSorting(
        sortConfig?.id ?? "",
        sortConfig?.desc ? "desc" : "asc",
      );
    },
    [setPolicyResearchSorting],
  );

  const calculateHeight = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const bottomPadding = 12;
      const calculatedHeight = viewportHeight - rect.top - bottomPadding;
      setTableHeight(Math.max(300, calculatedHeight));
    }
  }, []);

  useEffect(() => {
    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [calculateHeight]);

  const handleLoadMore = useCallback(() => {
    fetchPolicyResearch(true);
  }, [fetchPolicyResearch]);

  const getOptionLabel = useCallback(
    (columnId: string, value: string): string => {
      // Policy state codes come back lowercase from the backend; STATE_CODE_TO_NAME's keys (and
      // the State dropdown's option values, sourced from getPayers()) are uppercase.
      if (columnId === "state")
        return STATE_CODE_TO_NAME[value.toUpperCase()] || value;
      if (
        columnId === "payer" ||
        columnId === "line_of_business" ||
        columnId === "document_type" ||
        columnId === "status"
      )
        return toStartCase(value);
      return value;
    },
    [],
  );

  const getUniqueColumnValues = useCallback(
    (columnId: string): string[] => {
      if (columnId === "status") return POLICY_STATUSES;
      // "state" holds an array (a policy can apply to multiple states) -- flatten it
      // alongside the plain-string columns so both shapes produce the same option list.
      const values = (policyResearchData.policies || [])
        .flatMap((policy) => {
          const raw = (policy as unknown as Record<string, unknown>)[columnId];
          return Array.isArray(raw) ? raw : [raw];
        })
        .filter(
          (value): value is string => typeof value === "string" && value !== "",
        );
      return Array.from(new Set(values)).sort();
    },
    [policyResearchData.policies],
  );

  const columnFiltersConfig: ColumnFiltersConfig = useMemo(
    () => ({
      filters: policyResearchColumnFilters,
      getOptions: getUniqueColumnValues,
      getOptionLabel,
      onApply: setPolicyResearchColumnFilter,
      onClear: clearPolicyResearchColumnFilter,
      filterableColumns: FILTERABLE_COLUMNS,
      columnFilterTypes: COLUMN_FILTER_TYPES,
      columnFilterPlaceholders: COLUMN_FILTER_PLACEHOLDERS,
      isColumnPinned: isPolicyResearchColumnRequired,
      onTogglePin: (columnId) =>
        togglePolicyResearchRequiredColumn(
          columnId as PolicyResearchTableColumnId,
        ),
      canPinColumn: (columnId) =>
        !POLICY_RESEARCH_TABLE_COLUMN_OPTIONS.find((col) => col.id === columnId)
          ?.mandatory,
    }),
    [
      policyResearchColumnFilters,
      getUniqueColumnValues,
      getOptionLabel,
      setPolicyResearchColumnFilter,
      clearPolicyResearchColumnFilter,
      isPolicyResearchColumnRequired,
      togglePolicyResearchRequiredColumn,
    ],
  );

  const handleOpenInNewTab = useCallback(
    async (e: React.MouseEvent, documentId: string, sourceUrl: string) => {
      e.stopPropagation();

      if (sourceUrl?.endsWith(".html")) {
        window.open(sourceUrl, "_blank", "noopener,noreferrer");
        return;
      }

      // Open the tab synchronously, within the click gesture, so it isn't
      // popup-blocked once we go async below. Note: 'noopener' must NOT be
      // passed here — browsers return null instead of a window handle when
      // it's set, which would make the code below a no-op. We fully control
      // the navigation target (our own blob URL), so there's no tabnabbing
      // risk in keeping the opener reference.
      const newTab = window.open("", "_blank");

      try {
        setLoadingDocId(documentId);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(sourceUrl, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        if (newTab) {
          newTab.location.href = blobUrl;
        }
      } catch {
        newTab?.close();
        notify.showError(
          "Unable to open this policy document — the source site is not responding. Please try again later.",
        );
      } finally {
        setLoadingDocId(null);
      }
    },
    [notify],
  );

  // Opens the row's policy in the full-width policy panel (with the state/payer filters
  // and this policy pre-selected) and swaps the filters/table for the "Ask Reve"
  // welcome + chat input, mirroring ContractPreview's "Ask Reve" flow for contracts.
  const handleAskAboutPolicy = useCallback(
    (row: IPolicyPreviewItem) => {
      setPolicyToPreview(row);
      setIsPoliciesDocumentsDrawerOpen(true);
      setIsAskingReveForPolicy(true);
    },
    [
      setPolicyToPreview,
      setFilters,
      setIsPoliciesDocumentsDrawerOpen,
      setIsAskingReveForPolicy,
    ],
  );

  const columns: SimpleColumnDef<IPolicyPreviewItem>[] = useMemo(
    () => [
      {
        id: "title",
        header: "Policy",
        accessorKey: "title",
        width: 340,
        minWidth: 280,
        cell: (value: string, row: IPolicyPreviewItem) => (
          <Row
            sx={{
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
              paddingLeft: "20px",
            }}
          >
            <PolicyTypeBadge sourceUrl={row.source_url} />
            <Typography noWrap title={value}>
              {value}
            </Typography>
          </Row>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        width: 120,
        minWidth: 110,
        // Derived per row server-side -- no field to sort on.
        enableSorting: false,
        cell: (value?: PolicyStatus) => <StatusChip status={value} fitContent />,
      },
      {
        id: "state",
        header: "State",
        accessorKey: "state",
        width: 160,
        minWidth: 120,
        cell: (value?: string | string[]) => {
          // Despite the string[] type, the backend has been seen sending a single
          // string for policies that apply to just one state -- normalize either shape.
          const states = Array.isArray(value) ? value : value ? [value] : [];
          const label = states.length
            ? states
                .map((code) => STATE_CODE_TO_NAME[code.toUpperCase()] || code)
                .join(", ")
            : "—";
          return (
            <Typography noWrap title={label}>
              {label}
            </Typography>
          );
        },
      },
      {
        id: "payer",
        header: "Payer",
        accessorKey: "payer",
        width: 200,
        minWidth: 180,
        cell: (value?: string) => (
          <Typography noWrap title={value}>
            {value ? toStartCase(value) : "—"}
          </Typography>
        ),
      },
      {
        id: "plan_name",
        header: "Plan Name",
        accessorKey: "plan_name",
        width: 200,
        minWidth: 160,
        cell: (value?: string | null) => (
          <Typography noWrap title={value ?? undefined}>
            {value || "—"}
          </Typography>
        ),
      },
      {
        id: "plan_policy_id",
        header: "Plan Policy ID",
        accessorKey: "plan_policy_id",
        width: 150,
        minWidth: 130,
        cell: (value?: string | null) => (
          <Typography noWrap title={value ?? undefined}>
            {value || "—"}
          </Typography>
        ),
      },
      {
        id: "line_of_business",
        header: "Line of Business",
        accessorKey: "line_of_business",
        width: 160,
        minWidth: 140,
        cell: (value?: string) => (
          <Typography noWrap title={value}>
            {value ? toStartCase(value) : "—"}
          </Typography>
        ),
      },
      {
        id: "document_type",
        header: "Document Type",
        accessorKey: "document_type",
        width: 170,
        minWidth: 140,
        cell: (value?: string | null) => (
          <Typography noWrap title={value ?? undefined}>
            {value ? toStartCase(value) : "—"}
          </Typography>
        ),
      },
      {
        id: "date_of_business",
        header: "Effective Date",
        accessorKey: "date_of_business",
        width: 160,
        minWidth: 140,
        cell: (value?: string | null) => (
          <Typography noWrap>{value || "—"}</Typography>
        ),
      },
      {
        id: "expiration_date",
        header: "Expiration Date",
        accessorKey: "expiration_date",
        width: 150,
        minWidth: 130,
        cell: (value?: string | null) => (
          <Typography noWrap>{value || "—"}</Typography>
        ),
      },
      {
        id: "updated_on",
        header: "Updated On",
        accessorKey: "updated_on",
        width: 140,
        minWidth: 120,
        // Show just the date part of the "YYYY-MM-DD HH:MM:SS.ffffff" timestamp.
        cell: (value?: string | null) => (
          <Typography noWrap title={value ?? undefined}>
            {value ? value.slice(0, 10) : "—"}
          </Typography>
        ),
      },
      {
        id: "hcpcs_codes",
        header: "HCPCS Codes",
        accessorKey: "hcpcs_codes",
        width: 200,
        minWidth: 140,
        enableSorting: false,
        cell: renderCodes,
      },
      {
        id: "icd_10_codes",
        header: "ICD-10 Codes",
        accessorKey: "icd_10_codes",
        width: 200,
        minWidth: 140,
        enableSorting: false,
        cell: renderCodes,
      },
      {
        id: "actions",
        header: "",
        width: 88,
        minWidth: 88,
        enableSorting: false,
        cell: (_value: undefined, row: IPolicyPreviewItem) => (
          <Row sx={{ alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Ask about this policy" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAskAboutPolicy(row);
                }}
              >
                <ChatIcon />
              </IconButton>
            </Tooltip>
            {loadingDocId === row.document_id ? (
              <IconButton size="small" disabled>
                <CircularProgress size={14} />
              </IconButton>
            ) : (
              <Tooltip title="Open policy in new tab" arrow>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleOpenInNewTab(e, row.document_id, row.source_url)
                  }
                >
                  <OpenInNewIcon size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Row>
        ),
      },
    ],
    [loadingDocId, handleOpenInNewTab, handleAskAboutPolicy],
  );

  // Filter columns based on visibility settings, pinned (required) columns first
  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) =>
      isPolicyResearchColumnVisible(col.id),
    );
    const pinned = visible.filter((col) =>
      isPolicyResearchColumnRequired(col.id),
    );
    const unpinned = visible.filter(
      (col) => !isPolicyResearchColumnRequired(col.id),
    );
    return [...pinned, ...unpinned];
  }, [
    columns,
    isPolicyResearchColumnVisible,
    isPolicyResearchColumnRequired,
    policyResearchColumnSettings,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        overflowX: "auto",
        background: "white",
        marginLeft: "12px",
        marginRight: "12px",
        marginBottom: "12px",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: theme.palette.divider,
        width: "calc(100% - 24px)",
      }}
    >
      <CoreVirtualizedTable<IPolicyPreviewItem>
        isLoading={isLoadingPolicyResearch}
        data={policyResearchData.policies}
        columns={visibleColumns}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        height={`${tableHeight}px`}
        minWidth={500}
        getRowId={(row) => row.document_id}
        emptyMessage="No policies found"
        onLoadMore={handleLoadMore}
        hasMore={policyResearchHasMoreData}
        isLoadingMore={policyResearchIsLoadingMore}
        columnFilters={columnFiltersConfig}
        disableLastOfTypeStyles
        isResizable
        pinColumns
      />
    </div>
  );
});
